import React, { useState } from "react";
import { Chip, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

/**
 * Premium compact badge for the public lead-tracking number
 * (DCCL<YYMMDD>-<SEQ>). Used everywhere the operator needs the
 * permanent business reference — list rows, drawer header, detail page,
 * pipeline cards, calendar tooltips, follow-up tasks, emails, etc.
 *
 *   • Monospace lining so digits and the date segment align visually.
 *   • Tonal palettes — navy default, gold accent on hover.
 *   • Click → caller-supplied handler (typically opens the lead).
 *   • Cmd/Ctrl-click → copies the number to clipboard (silent, with a
 *     200ms "Copied" tooltip).
 *   • Renders nothing when the value is empty — keeps the existing
 *     layout intact for legacy rows that haven't been backfilled yet.
 */
export type LeadNumberBadgeVariant = "default" | "header" | "muted";

interface Props {
  value?: string | null;
  variant?: LeadNumberBadgeVariant;
  onClick?: () => void;
  className?: string;
}

const FONT_STACK = "'SF Mono','JetBrains Mono','Roboto Mono',Menlo,Consolas,monospace";

export default function LeadNumberBadge({ value, variant = "default", onClick, className }: Props) {
  const [copied, setCopied] = useState(false);

  if (!value) return null;

  const handleClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
      try {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1100);
      } catch { /* no-op */ }
      return;
    }
    onClick?.();
  };

  const styles = variantStyles(variant);

  return (
    <Tooltip
      title={copied ? "Copied" : "Lead number · ⌘/Ctrl-click to copy"}
      arrow
      placement="top"
    >
      <Chip
        label={value}
        size="small"
        onClick={onClick || copied ? handleClick : undefined}
        className={className}
        sx={{
          height: styles.height,
          fontSize: styles.fontSize,
          fontFamily: FONT_STACK,
          fontWeight: 700,
          letterSpacing: 0.4,
          color: styles.color,
          bgcolor: styles.bgcolor,
          border: `1px solid ${styles.border}`,
          borderRadius: 1,
          cursor: onClick ? "pointer" : "default",
          transition: "all 0.16s ease",
          "& .MuiChip-label": { px: 0.9 },
          "&:hover": onClick ? {
            bgcolor: styles.hoverBg,
            borderColor: styles.hoverBorder,
            color: styles.hoverColor,
            boxShadow: styles.shadow,
          } : {},
        }}
        icon={onClick
          ? <ContentCopyIcon sx={{ fontSize: "10px !important", color: `${styles.color} !important`, opacity: 0.5 }} />
          : undefined}
      />
    </Tooltip>
  );
}

function variantStyles(variant: LeadNumberBadgeVariant) {
  if (variant === "header") {
    // White-on-navy: lives inside the dark drawer/page headers.
    return {
      height: 22,
      fontSize: 11,
      color: "#fff",
      bgcolor: "rgba(255,255,255,0.10)",
      border: "rgba(255,255,255,0.22)",
      hoverBg: "rgba(201,168,76,0.18)",
      hoverBorder: "#D4AF37",
      hoverColor: "#D4AF37",
      shadow: "0 2px 8px rgba(201,168,76,0.25)",
    };
  }
  if (variant === "muted") {
    return {
      height: 18,
      fontSize: 10,
      color: "#475569",
      bgcolor: "#f1f5f9",
      border: "#e2e8f0",
      hoverBg: "#e2e8f0",
      hoverBorder: "#cbd5e1",
      hoverColor: "#1E4D8F",
      shadow: "none",
    };
  }
  // default — navy text on a slate background, gold on hover.
  return {
    height: 20,
    fontSize: 10.5,
    color: "#1E4D8F",
    bgcolor: "#eef2f7",
    border: "#d6deea",
    hoverBg: "#fef9ec",
    hoverBorder: "#D4AF37",
    hoverColor: "#1E4D8F",
    shadow: "0 2px 8px rgba(13,27,42,0.10)",
  };
}
